// ────────────────────────────────────────────────────────────────────
// ImportConsole — the staff half of /intake (2026-08-04).
//
// /intake used to be an Explorer clone: folders, drag-drop, multi-select,
// right-click. "My computer, but better." The desk landing now sends people
// here with a card that says นำข้อมูลเข้าสู่ระบบ, and what they arrived at was
// an empty Inbox with no import affordance anywhere on it — the verb was
// hidden inside a right-click menu, in English, on a file you first had to
// upload and then find. The metaphor was a filing cabinet; the job is an
// import.
//
// This surface is two things: somewhere to drop files, and a queue of files
// still awaiting a decision.
//
// ── Why this shape does not become the old Inbox again ──────────────────
// Dropping first and asking "what is this?" afterwards is the shape closest to
// the thing that failed, so three properties carry the design:
//
//   The queue EMPTIES. It shows status === "received" only — a file leaves the
//   moment it is filed or imported, and the backend already maintains that
//   transition (intake.py sets "filed" on filing and "imported" on both import
//   paths). "ไม่มีไฟล์รอจัดการ" is therefore a finish line, not an empty
//   folder, which is exactly what the old Inbox could never say.
//
//   Impossible actions are DISABLED WITH A REASON, never hidden. The old
//   context menu simply omitted "Import to asset…" for a PDF, leaving "why
//   can't I import this" unanswered and unanswerable. A greyed button that
//   says ต้องเป็นไฟล์ตาราง answers it before it is asked.
//
//   The asset precondition is a STEP, not a wall. Thickness import requires
//   the file to be filed to an asset — production answers 400 "File it to an
//   asset first, then import into that asset" otherwise (verified against
//   app.xzynexa.com on 2026-08-04). Nothing on the old screen said so. Here,
//   pressing ค่าอ่านความหนา on an unfiled file opens the asset picker as step
//   one of the import rather than letting anyone walk into that 400.
//
// Register import is deliberately exempt: it CREATES the plant/area/asset
// hierarchy, so requiring an asset first would be circular.
//
// The file room is not deleted — it still does real work (trash, per-asset
// documents, search, revisions) and lives one click below.
// ────────────────────────────────────────────────────────────────────
import { useState } from "react";
import { UploadCloud, FileSpreadsheet, FileText, Database, Building2, ArrowRight } from "lucide-react";
import type { IntakeFile } from "@/lib/types";
import type { Classification } from "@/lib/importClassify";

const TABULAR = /\.(xlsx|xls|csv)$/i;
const MAX_MB = 200;

/** Only a spreadsheet can be parsed into rows. A PDF report is a document —
 *  real, wanted, and simply not a source of readings. */
export function isTabular(f: IntakeFile): boolean {
  return f.kind === "data" || TABULAR.test(f.filename);
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ImportConsole({
  pending, totals, classified = {}, onPickFiles, onImport, onRegisterImport, onFileToAsset, onOpenFileRoom,
}: {
  pending: IntakeFile[];
  totals: { all: number; trash: number };
  /** file id -> what its columns say it is. Absent = not read yet. */
  classified?: Record<number, Classification | undefined>;
  onPickFiles: () => void;
  onImport: (f: IntakeFile) => void;
  onRegisterImport: (f: IntakeFile) => void;
  onFileToAsset: (f: IntakeFile) => void;
  onOpenFileRoom: () => void;
}): JSX.Element {
  return (
    <div className="p-6 flex flex-col gap-5" style={{ maxWidth: 860, margin: "0 auto", width: "100%" }}>
      <DropZone onClick={onPickFiles} />

      <div>
        <div className="font-mono uppercase mb-2" style={{ fontSize: 9.5, letterSpacing: ".16em", color: "var(--ink-4)" }}>
          ไฟล์ที่รอจัดการ{pending.length > 0 && ` · ${pending.length}`}
        </div>

        {pending.length === 0 ? (
          <div data-testid="pending-empty" className="px-3.5 py-4 font-mono"
               style={{ fontSize: 11.5, color: "var(--ink-3)", border: "1px solid var(--hairline)", background: "var(--paper-2)" }}>
            ไม่มีไฟล์รอจัดการ — ทุกไฟล์ที่ส่งเข้ามาถูกนำเข้าหรือจัดเก็บแล้ว
          </div>
        ) : (
          <div style={{ border: "1px solid var(--hairline)", background: "var(--paper-2)" }}>
            {pending.map((f) => (
              <PendingRow key={f.id} file={f} verdict={classified[f.id]}
                          onImport={onImport} onRegisterImport={onRegisterImport} onFileToAsset={onFileToAsset} />
            ))}
          </div>
        )}
      </div>

      <button data-testid="open-file-room" onClick={onOpenFileRoom}
              className="flex items-center justify-between px-3.5 py-2.5 transition-colors hover:bg-[rgba(31,78,121,0.04)]"
              style={{ border: "1px solid var(--hairline)" }}>
        <span className="font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>
          ห้องไฟล์ทั้งหมด · {totals.all} ไฟล์{totals.trash > 0 && ` · ถังขยะ ${totals.trash}`}
        </span>
        <ArrowRight size={13} style={{ color: "var(--ink-4)" }} />
      </button>
    </div>
  );
}

function DropZone({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button data-testid="drop-zone" onClick={onClick}
            className="w-full flex flex-col items-center justify-center gap-1.5 py-9 transition-colors hover:bg-[rgba(31,78,121,0.03)]"
            style={{ border: "1px dashed var(--hairline)", background: "var(--paper-2)" }}>
      <UploadCloud size={20} style={{ color: "var(--accent)" }} />
      <span className="font-mono" style={{ fontSize: 12.5, color: "var(--ink-1)", fontWeight: 600 }}>
        ลากไฟล์มาวางที่นี่ หรือกดเพื่อเลือกไฟล์
      </span>
      <span className="font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>
        Excel · CSV · PDF · DWG · รูป — สูงสุด {MAX_MB} MB ต่อไฟล์
      </span>
    </button>
  );
}

function PendingRow({ file, verdict, onImport, onRegisterImport, onFileToAsset }: {
  file: IntakeFile;
  verdict: Classification | undefined;
  onImport: (f: IntakeFile) => void;
  onRegisterImport: (f: IntakeFile) => void;
  onFileToAsset: (f: IntakeFile) => void;
}): JSX.Element {
  const tabular = isTabular(file);
  const [showAll, setShowAll] = useState(false);

  // Unfiled thickness imports need an asset first — import-preview answers 400
  // otherwise. Asking for it here makes the precondition a step, not a wall.
  const runThickness = () => (file.asset_id ? onImport(file) : onFileToAsset(file));

  const decided = tabular && verdict && verdict.kind !== "unknown" ? verdict : null;
  const asking = tabular && (!decided || showAll);

  return (
    <div data-testid={`pending-${file.id}`} className="px-3.5 py-3 border-b last:border-b-0"
         style={{ borderColor: "var(--hairline)" }}>
      <div className="flex items-center gap-2 mb-2 min-w-0">
        {tabular ? <FileSpreadsheet size={14} style={{ color: "var(--accent)", flexShrink: 0 }} />
                 : <FileText size={14} style={{ color: "var(--ink-4)", flexShrink: 0 }} />}
        <span className="font-mono truncate" style={{ fontSize: 12.5, color: "var(--ink-1)", fontWeight: 600 }}>
          {file.filename}
        </span>
        <span className="font-mono shrink-0" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>
          {fmtSize(file.size_bytes)}
          {file.asset_tag && ` · ${file.asset_tag}`}
        </span>
      </div>

      {/* A document is not a question. The only thing still open is where it
          belongs, so that is the only thing asked. */}
      {!tabular && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>
            เอกสาร — เหลือแค่เลือกว่าผูกกับ asset ไหน
          </span>
          <Act testId="act-document" icon={<FileText size={11} />} label="เลือก asset"
               onClick={() => onFileToAsset(file)} />
        </div>
      )}

      {/* Read, but not yet answered. Saying so beats showing a verdict that has
          not been reached, and beats an empty row that looks broken. */}
      {tabular && !verdict && (
        <div className="font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>
          กำลังอ่านคอลัมน์ในไฟล์…
        </div>
      )}

      {decided && !showAll && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <Act testId="act-primary"
                 icon={decided.kind === "thickness" ? <Database size={11} /> : <Building2 size={11} />}
                 label={decided.kind === "thickness" ? "นำเข้าเป็น ค่าอ่านความหนา" : "นำเข้าเป็น ทะเบียนอุปกรณ์"}
                 primary
                 onClick={() => (decided.kind === "thickness" ? runThickness() : onRegisterImport(file))} />
            <button data-testid="act-other" onClick={() => setShowAll(true)}
                    className="font-mono underline underline-offset-2"
                    style={{ fontSize: 10.5, color: "var(--ink-3)" }}>
              ไม่ใช่? เลือกเอง
            </button>
          </div>
          {/* The evidence. A verdict you cannot check is just a different way
              of being told what to think. */}
          <div className="font-mono mt-1.5 truncate" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
            จากคอลัมน์ {decided.matched.join(" · ")}
          </div>
        </>
      )}

      {asking && verdict && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>นี่คือ?</span>
          <Act testId="act-thickness" icon={<Database size={11} />} label="ค่าอ่านความหนา" onClick={runThickness} />
          <Act testId="act-register" icon={<Building2 size={11} />} label="ทะเบียนอุปกรณ์"
               onClick={() => onRegisterImport(file)} />
          <Act testId="act-document" icon={<FileText size={11} />} label="เก็บเป็นเอกสาร"
               onClick={() => onFileToAsset(file)} />
        </div>
      )}
    </div>
  );
}

function Act({ testId, icon, label, onClick, primary }: {
  testId: string; icon: JSX.Element; label: string; onClick: () => void; primary?: boolean;
}): JSX.Element {
  return (
    <button data-testid={testId} onClick={onClick}
            className="font-mono inline-flex items-center gap-1 px-2 py-1 transition-colors"
            style={primary
              ? { fontSize: 10.5, border: "1px solid var(--accent)", color: "var(--ink-1)",
                  background: "var(--accent)", fontWeight: 700 }
              : { fontSize: 10.5, border: "1px solid var(--hairline)", color: "var(--ink-1)",
                  background: "var(--paper)" }}>
      {icon} {label}
    </button>
  );
}
